/**
 * Re-run a recorded model call against another model, and the re-runs made so
 * far. A re-run sends exactly what the run sent — its prompt, system prompt and
 * images — and is stored apart: no student, teacher or analysis ever reads it.
 * See docs/FeatureDesigns/PipelineInfra/model-run-replay-target.md.
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { formatDateTimePrecise } from '@shared-ui/components';
import { adminApi } from '../../api/adminApi';
import type { ModelOutput, ModelRunReplaySummary } from '../../types';
import '../operations/RerunDialog.css';

const formatCost = (usd: number) => `$${usd.toFixed(4)}`;
const formatSeconds = (ms: number) => `${(ms / 1000).toFixed(1)}s`;

const OUTCOME_LABEL: Record<ModelRunReplaySummary['outcome'], { text: string; color: string }> = {
  ok: { text: 'ok', color: 'var(--success, #16a34a)' },
  parse_error: { text: 'parse error', color: 'var(--warning, #d97706)' },
  call_error: { text: 'call failed', color: 'var(--error, #dc2626)' },
};

type UserName = (id?: string) => string;

/** The run's re-runs, and why it cannot be re-run when it cannot. One cache
 *  entry, read by the header button and the list alike. */
function useReplays(outputId: string) {
  return useQuery({
    queryKey: ['modelRunReplays', outputId],
    queryFn: () => adminApi.getModelRunReplays(outputId),
  });
}

/** The banner on a re-run's own page: what it is, and what it was re-run from. */
export function ReplayBanner({ output, getUserName }: { output: ModelOutput; getUserName: UserName }) {
  const of = output.replayOf;
  if (!of) return null;
  return (
    <div className="card" style={{ borderLeft: '4px solid #8b5cf6' }}>
      <strong>Admin re-run</strong> — not part of any analysis, and never shown to a student or teacher.
      It re-sent the call of run{' '}
      <Link to={`/model-data/${of.outputId}`}>…{of.outputId.slice(-8)}</Link>{' '}
      (served by <strong>{of.provider}</strong>) to <strong>{output.parameters.provider}</strong>
      {of.requestedBy && <> · by {getUserName(of.requestedBy)}</>} · {formatDateTimePrecise(of.requestedAt)}.
      {of.comment && (
        <div style={{ marginTop: 8, whiteSpace: 'pre-wrap' }}>
          <span style={{ color: 'var(--text-tertiary)' }}>Comment: </span>{of.comment}
        </div>
      )}
    </div>
  );
}

/** Header action: the Re-run button, its dialog, and a link to the result. Not
 *  offered on a re-run itself — re-run the original. */
export function ReplayButton({ output }: { output: ModelOutput }) {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [lastReplayId, setLastReplayId] = useState<string | null>(null);
  const { data } = useReplays(output.id);
  // The re-run is a queued job (model-run-replay); `replayModelRun` queues it
  // and waits for it (waitForAsyncJob). The call lives here, not in the dialog,
  // so closing the dialog loses nothing and cannot invite a second paid call.
  const replay = useMutation({
    mutationFn: (req: ReplayRequest) => adminApi.replayModelRun(output.id, req),
    onSuccess: ({ replayId }) => {
      queryClient.invalidateQueries({ queryKey: ['modelRunReplays', output.id] });
      setLastReplayId(replayId);
      setOpen(false);
    },
  });
  const running = replay.isPending;
  const error = replay.isError ? (replay.error instanceof Error ? replay.error.message : String(replay.error)) : null;
  if (output.source === 'replay') return null;

  return (
    <>
      {lastReplayId && (
        <Link to={`/model-data/${lastReplayId}`} className="new-submission-link">View re-run</Link>
      )}
      <button
        className="rerun-button"
        onClick={() => setOpen(true)}
        disabled={!data || !!data.blocker}
        title={data?.blocker ? `Cannot re-run: ${data.blocker}` : running ? 'A re-run is in progress' : 'Send this run\'s prompt and images to another model'}
      >
        {running ? 'Re-running…' : 'Re-run'}
      </button>
      {/* A failure after the dialog was closed still has to be seen. */}
      {!open && error && <span className="rerun-error" style={{ color: 'var(--error, #dc2626)', fontSize: 13 }}>Re-run failed: {error}</span>}
      {open && (
        <ReplayDialog
          output={output}
          running={running}
          error={error}
          onRun={req => replay.mutate(req)}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  );
}

type ReplayRequest = Parameters<typeof adminApi.replayModelRun>[1];

function ReplayDialog({ output, running, error, onRun, onClose }: {
  output: ModelOutput;
  running: boolean;
  error: string | null;
  onRun: (req: ReplayRequest) => void;
  onClose: () => void;
}) {
  const [provider, setProvider] = useState('');
  const [maxTokens, setMaxTokens] = useState('');
  const [comment, setComment] = useState('');

  const { data: providers } = useQuery({
    queryKey: ['availableProvidersDetailed'],
    queryFn: adminApi.getAvailableProvidersDetailed,
  });
  const choices = (providers ?? []).filter(p => p.available && p.key !== output.parameters.provider);
  const originalMaxTokens = output.parameters.actual?.maxTokens;

  const run = () => onRun({
    provider,
    ...(maxTokens.trim() ? { maxTokens: Number(maxTokens) } : {}),
    ...(comment.trim() ? { comment: comment.trim() } : {}),
  });

  return (
    <div className="modal-overlay">
      <div className="modal-content rerun-modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Re-run with another model</h2>
          <button className="close-btn" onClick={onClose} aria-label="Close">×</button>
        </div>
        <div className="modal-body">
          <p style={{ color: 'var(--text-secondary)', marginTop: 0 }}>
            Sends exactly what this run sent — its prompt, system prompt and images — to the model you pick.
            The result is kept for comparison only: it changes no analysis and no student or teacher sees it.
            Thinking and reasoning settings are the chosen model's own. <strong>This is one paid model call.</strong>
          </p>

          <div className="rerun-section-label">Model</div>
          <select
            className="rerun-pipeline-select"
            value={provider}
            onChange={e => setProvider(e.target.value)}
            disabled={running}
            style={{ marginBottom: 16 }}
          >
            <option value="">Select a model…</option>
            {choices.map(p => (
              <option key={p.key} value={p.key}>{p.displayName || p.key} ({p.modelName})</option>
            ))}
          </select>

          <div className="rerun-section-label">Max output tokens</div>
          <input
            className="replay-field"
            type="number"
            min={1}
            value={maxTokens}
            placeholder={originalMaxTokens ? `${originalMaxTokens} (as the run)` : 'As the run'}
            onChange={e => setMaxTokens(e.target.value)}
            disabled={running}
          />

          <div className="rerun-section-label">Comment <span style={{ textTransform: 'none', fontWeight: 400 }}>— why you are re-running it (optional)</span></div>
          <textarea
            className="replay-field"
            rows={3}
            maxLength={2000}
            value={comment}
            onChange={e => setComment(e.target.value)}
            disabled={running}
            placeholder="e.g. Does gpt-5-mini read the text on this page better than Gemini?"
          />

          {running && (
            <p style={{ color: 'var(--text-tertiary)', marginBottom: 0 }}>
              Queued and running — usually under two minutes. You can close this: the re-run
              carries on, and a “View re-run” link appears here when it is done (it is also
              listed under Re-runs with other models).
            </p>
          )}
          {error && (
            <p style={{ color: 'var(--error, #dc2626)', marginBottom: 0 }}>{error}</p>
          )}
        </div>
        <div className="modal-footer">
          <div className="footer-spacer" />
          <button className="btn-cancel" onClick={onClose}>{running ? 'Close' : 'Cancel'}</button>
          <button className="btn-save" onClick={run} disabled={!provider || running}>
            {running ? 'Running…' : 'Run'}
          </button>
        </div>
      </div>
    </div>
  );
}

/** The run's re-runs (on a re-run's page: the other re-runs of the same run).
 *  Shown only when there are any. */
export function ModelRunReplays({ output, getUserName }: { output: ModelOutput; getUserName: UserName }) {
  const { data } = useReplays(output.id);
  const replays = data?.replays ?? [];
  const isReplay = output.source === 'replay';
  if (replays.length === 0 || (isReplay && replays.length <= 1)) return null;

  return (
    <div className="card">
      <h3>{isReplay ? 'Other re-runs of the same run' : 'Re-runs with other models'}</h3>
      <div className="table-scroll" style={{ overflowX: 'auto' }}>
        <table className="data-table">
          <thead>
            <tr>
              <th>When</th><th>Model</th><th>Outcome</th><th>Cost</th><th>Latency</th>
              <th>Tokens in / out</th><th>By</th><th>Comment</th><th />
            </tr>
          </thead>
          <tbody>
            {replays.map(r => {
              const outcome = OUTCOME_LABEL[r.outcome];
              const current = r.id === output.id;
              return (
                <tr key={r.id} style={current ? { background: 'var(--bg-secondary)' } : undefined}>
                  <td>{formatDateTimePrecise(r.generatedAt)}</td>
                  <td>{r.provider}</td>
                  <td style={{ color: outcome.color }} title={r.error}>
                    {outcome.text}{r.stopReason && r.stopReason !== 'stop' ? ` (${r.stopReason})` : ''}
                  </td>
                  <td>{formatCost(r.cost)}</td>
                  <td>{formatSeconds(r.latencyMs)}</td>
                  <td>{r.inputTokens.toLocaleString()} / {r.outputTokens.toLocaleString()}</td>
                  <td>{r.replayOf.requestedBy ? getUserName(r.replayOf.requestedBy) : '-'}</td>
                  <td style={{ whiteSpace: 'pre-wrap', maxWidth: 320 }}>{r.replayOf.comment ?? ''}</td>
                  <td>{current ? 'this one' : <Link to={`/model-data/${r.id}`}>Open</Link>}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
